import { defineComponent as _defineComponent } from 'vue'
import { withDefaults, ref, computed, inject, getCurrentInstance } from 'vue';
import {
  IStepComponent, Toolbar, FullScreenToggle, Step, StepPager, Action, Splitter,
  ClearAfterFunction, GetService, IFileSource, IClientMapping, Grid,
  CellClassParams, GetContextMenuItemsParams, MenuItemDef
} from '@samc/centricity-core';
import { IMatchedLoanItems, IMatchedLoanItemsCount, IMatchedLoans } from './Interfaces';
import { AjaxHelper, errToMessage, pluralize } from '@samc/common';
import { useReactiveInject } from '@/useReactiveInject';


export default /*#__PURE__*/_defineComponent({
  __name: 'MatchingStep',
  props: {
    title: { type: String, required: true, default: 'Matched Loans' },
    exportFileName: { type: [String, null], required: true, default: null },
    verificationToken: { type: [String, null], required: true, default: null },
    stagingUrl: { type: [String, null], required: true, default: null }
  },
  setup(__props: any) {

const props = __props as { title: string, exportFileName: string | null, verificationToken: string | null, stagingUrl: string | null };

const instance = getCurrentInstance();



const id = ref('matching');

  //#region Basic Properties
const isValid = ref(true);
const isDirty = ref(false);
const isReadonly = ref(false);
const initialized = ref(false);
const panes = ref<number[]>([.20, .45, .35]);
const matchedLoans = ref<IMatchedLoanItems[]>([]);
const unMatchedLoans = ref<IMatchedLoanItems[]>([]);
const matchedLoansCount = ref<IMatchedLoanItemsCount[]>([]);

const description = computed(() => {
  if (!initialized.value) return ''; // Return nothing if this step has not been initialized

  if (matchedLoans.value.length > 0) {
    const row = pluralize(matchedLoans.value.length, '{0} Loans Found', '{0} Loans Found');
    return `${row}`;
  } else {
    return 'No Loans Found';
  }
});

//#region Injectors
const clearAfter = inject<ClearAfterFunction>('clearAfter');
const getService = inject<GetService>('getService');

const fileSource = useReactiveInject<IFileSource | null>('fileSource');
const mapping = useReactiveInject<IClientMapping | null>('mapping');
//#endregion

const initialize = async () => {
  const service = getService?.('staging');
  if (service !== null && fileSource.value !== null && fileSource.value !== undefined) {
    let noErrors = true;
    try {
      instance?.proxy?.$loading('Fetching matched loans');
      const helper = new AjaxHelper(props.stagingUrl ?? '', props.verificationToken, '', null);
      const result = await helper.getData<IMatchedLoans>(`/matchedLoans/${fileSource.value.id}`);
      matchedLoans.value = result.loanItemDetails.filter(x => x.isFound == true && x.description === null || x.description === '');
      unMatchedLoans.value = result.loanItemDetails.filter(x => x.isFound == false || x.description !== null && x.description !== '');
      matchedLoansCount.value = result.loanCountDetails;
      initialized.value = true;
      if (matchedLoans.value.length == 0) {
        isValid.value = false;
      }
      if (matchedLoans.value.length > 0 && unMatchedLoans.value.length > 0) {
        instance?.proxy?.$alert.warning('Only matched and unique loans will be imported.');
        isValid.value = true;
      }
    } catch (err: any) {
      instance?.proxy?.$log('Error occurred during loading data matched loan step', err);
      instance?.proxy?.$alert({
        type: 'error',
        title: 'Error occurred during loading matched loan step',
        errors: errToMessage(err),
        closingOption: 'holdOpen'
      });
      noErrors = false;
    } finally {
      instance?.proxy?.$loading();
    }

    if (noErrors) {
      initialized.value = true;
    }
    return noErrors;
  }
  return false;
};

/**
   * Method is called when this step is entered
   **/
const enter = async (): Promise<void> => {
  // Nothing needs to happen
};

/**
 * Method is called when this step is skipped by either disabled or readonly
 */
const skip = async (): Promise<void> => {
  // Nothing needs to happen
}

/**
 * Intialize events for the event bus
 **/
const initializeEvents = async (): Promise<void> => {
  // Do Nothing
};

/**
 * Remove events from the event bus
 **/
const clearEvents = async (): Promise<void> => {
  // Do Nothing
};

/**
 * Method is called when the step is left
 */
const leave = async (dir: -1 | 1): Promise<boolean> => {
  return true;
};

/**
 * Method is meant to be ran if the mapping component needs to clear this step
 **/
const clear = async () => {
  isValid.value = true;
  isDirty.value = false;
  isReadonly.value = false;
  initialized.value = false;
}

const contextMenuItems = (params: GetContextMenuItemsParams) => {
  const defaultMenu: (string | MenuItemDef)[] = ['copy', 'copyWithHeaders', 'paste'];
  return defaultMenu;
};

const cellClass = (params: CellClassParams): string | string[] => {
  return 'leftAlign';
};

const isColumnExists = (columnName: string): boolean => {
  const fieldMap = mapping.value?.details.filter(t =>
    t.internalFieldName?.toLowerCase().replaceAll(' ', '') === columnName?.toLowerCase().replaceAll(' ', '')
  );
  if (fieldMap != null && fieldMap?.length > 0) {
    return true;
  }
  return false;
};

/**
 * The wrapper for the component instance
 */
const component = computed<IStepComponent | null>(() => ({
  isValid: isValid.value,
  isDirty: isDirty.value,
  isReadonly: isReadonly.value,
  clear, initializeEvents, clearEvents,
  initialize, enter, skip, leave
}));

return { __sfc: true,instance, props, id, isValid, isDirty, isReadonly, initialized, panes, matchedLoans, unMatchedLoans, matchedLoansCount, description, clearAfter, getService, fileSource, mapping, initialize, enter, skip, initializeEvents, clearEvents, leave, clear, contextMenuItems, cellClass, isColumnExists, component, Toolbar, FullScreenToggle, Step, StepPager, Action, Splitter, Grid }
}

})